// Product margins from the real sales mix (Toast history x recipe costs),
// plus the weekday demand forecast — Ben's "visually appealing margins" and
// the first honest cut of forecasting, on actual data.
// CPG accounts get their own shape entirely (CpgMarginsPage): monthly margins
// by product and channel plus the monthly/yearly demand forecast, computed
// from wholesale orders — a brand has no weekdays and no POS. Detection reads
// the same signals Layout.tsx keys off, through the same query key, so no
// extra request fires. Restaurants keep everything below exactly as it was.
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import EntityToggle, { EntityView } from '../components/EntityToggle';
import { formatByCurrency } from '../lib/money';
import { Loader2, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import api, { restaurantApi } from '../services/api';
import CpgMarginsPage from './CpgMarginsPage';
import { isCpgSettings } from '../utils/cpg';
import {
  ComparePeriodBar,
  CoverageBanner,
  Delta,
  GatedCompareRow,
  NoData,
  rangeLabel,
  storedRestaurants,
  type ComparePair,
  type DateRange,
  type DetailLine,
} from '../components/PeriodCompare';
import { DeltaBars, HBarList, PairedBars, shortMoney, signedShortMoney } from '../components/analytics/Bars';

const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '—' : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

// Rows from the margins endpoint (Toast) or the wholesale aggregation, reduced
// to what the Mix and comparisons need.
interface MixItem { name: string; marginPct: number | null; weeklyProfitCents: number | null; soldPerWeek?: number }

interface ToastMarginsData { connected: boolean; windowDays: number; rows: any[]; unmatched: any[] }

const fetchMargins = async (params: Record<string, string | number>) =>
  (await api.get('/v1/integrations/toast/margins', { params })).data?.data as ToastMarginsData | undefined;

// Weekly-pace totals for one margins response. Paces (not raw totals) keep
// ranges of different lengths comparable without scaling any stored number.
// windowDays is PR #19/#20's coverage signal: 0 means this range had no
// recorded sales days (not a zero — no data). costedItems vs uncostedItems
// tells profit/margin apart from "selling but uncosted".
function summarize(data: ToastMarginsData | null | undefined) {
  if (!data) return null;
  let profitWk = 0;
  let salesWk = 0;
  let costedSalesWk = 0;
  let costedItems = 0;
  for (const r of data.rows || []) {
    const sales = (r.soldPerWeek || 0) * (r.avgPriceCents || 0);
    salesWk += sales;
    if (r.weeklyProfitCents !== null && r.weeklyProfitCents !== undefined) {
      profitWk += r.weeklyProfitCents;
      costedSalesWk += sales;
      costedItems += 1;
    }
  }
  const uncostedItems = (data.unmatched || []).length;
  for (const u of data.unmatched || []) salesWk += (u.soldPerWeek || 0) * (u.avgPriceCents || 0);
  return {
    windowDays: data.windowDays ?? 0,
    salesWk: Math.round(salesWk),
    costedSalesWk: Math.round(costedSalesWk),
    profitWk: Math.round(profitWk),
    blendedPct: costedSalesWk > 0 ? (profitWk / costedSalesWk) * 100 : null,
    // Share of sales that carry a recipe cost — the margin's denominator. When
    // it's below ~100%, profit ÷ (total) sales reads under the blended margin,
    // because uncosted items sit in sales but not in the margin base.
    coveragePct: salesWk > 0 ? (costedSalesWk / salesWk) * 100 : null,
    costedItems,
    uncostedItems,
  };
}

// Items present (and costed) in both periods, ordered by how much their
// weekly profit moved. Items on one side only are left out — no delta exists.
function profitMovers(a: ToastMarginsData, b: ToastMarginsData, n = 8) {
  const prevByName = new Map<string, any>((b.rows || []).map((r: any) => [r.toastName, r]));
  const out: Array<{ name: string; now: number; prev: number }> = [];
  for (const r of a.rows || []) {
    const prev = prevByName.get(r.toastName);
    if (!prev || r.weeklyProfitCents == null || prev.weeklyProfitCents == null) continue;
    if (r.weeklyProfitCents === prev.weeklyProfitCents) continue;
    out.push({ name: r.toastName, now: r.weeklyProfitCents, prev: prev.weeklyProfitCents });
  }
  return out.sort((x, y) => Math.abs(y.now - y.prev) - Math.abs(x.now - x.prev)).slice(0, n);
}

// The "Mix": where margin and profit concentrate — top and bottom ten of the
// full list, by margin % and by weekly profit (margin weighted by volume).
function MixSection({ items }: { items: MixItem[] }) {
  const costed = items.filter(
    (i) => i.marginPct !== null && i.weeklyProfitCents !== null
  ) as Array<{ name: string; marginPct: number; weeklyProfitCents: number; soldPerWeek?: number }>;
  if (costed.length < 2) return null;
  // Margin-% rankings need a volume floor: a once-a-month $1 ring-up must not
  // outrank the menu. Weekly profit is already volume-weighted, so it keeps
  // the full list.
  const steady = costed.filter((i) => (i.soldPerWeek ?? Infinity) >= 3);
  const marginPool = steady.length >= 2 ? steady : costed;
  const byMargin = [...marginPool].sort((a, b) => b.marginPct - a.marginPct);
  const byProfit = [...costed].sort((a, b) => b.weeklyProfitCents - a.weeklyProfitCents);
  const pctOf = (i: { name: string; marginPct: number }) => ({ name: i.name, value: i.marginPct });
  const profitOf = (i: { name: string; weeklyProfitCents: number }) => ({ name: i.name, value: i.weeklyProfitCents });
  const pctFmt = (v: number) => `${Math.round(v)}%`;
  const profitFmt = (v: number) => `${shortMoney(v)}/wk`;
  const lists = [
    { title: 'Top 10 by margin %', items: byMargin.slice(0, 10).map(pctOf), format: pctFmt, seriesLabel: 'Margin' },
    { title: 'Bottom 10 by margin %', items: [...byMargin].reverse().slice(0, 10).map(pctOf), format: pctFmt, seriesLabel: 'Margin' },
    { title: 'Top 10 by weekly profit', items: byProfit.slice(0, 10).map(profitOf), format: profitFmt, seriesLabel: 'Weekly profit' },
    { title: 'Bottom 10 by weekly profit', items: [...byProfit].reverse().slice(0, 10).map(profitOf), format: profitFmt, seriesLabel: 'Weekly profit' },
  ];
  return (
    <div className="card p-5">
      <h2 className="text-lg font-semibold text-slate-900">Mix</h2>
      <p className="text-sm text-slate-500 mb-4">
        Where margin and profit concentrate — best and worst of the list below. Weekly profit weights margin by volume.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {lists.map((l) => (
          <div key={l.title} className="rounded-lg bg-slate-50 p-4 min-w-0">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">{l.title}</p>
            <HBarList items={l.items} format={l.format} seriesLabel={l.seriesLabel} />
          </div>
        ))}
      </div>
    </div>
  );
}

function getCurrentRestaurantId(): string | null {
  try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
}

// The wrapper: CPG accounts → the monthly margins + forecast view; everyone
// else → the page exactly as it has always been (same pattern as PnlPage).
export default function MarginsPage() {
  const currentRestaurantId = getCurrentRestaurantId();
  const { data: fullRestaurant, isPending } = useQuery({
    queryKey: ['restaurant-current', currentRestaurantId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });

  if (isPending) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }
  return isCpgSettings(fullRestaurant?.settings) ? <CpgMarginsPage /> : <RestaurantMarginsPage />;
}

function RestaurantMarginsPage() {
  const [pair, setPair] = useState<ComparePair | null>(null);
  const stores = useMemo(storedRestaurants, []);
  const isMultiStore = stores.length > 1;

  const { data: margins, isLoading } = useQuery({
    queryKey: ['toast-margins'],
    queryFn: async () => (await api.get('/v1/integrations/toast/margins?days=28')).data?.data,
    retry: 2,
  });
  const { data: forecast } = useQuery({
    queryKey: ['toast-forecast'],
    queryFn: async () => (await api.get('/v1/integrations/toast/forecast')).data?.data,
  });

  const connected = !!margins?.connected;

  // Period comparison: both sides are explicit stored ranges.
  const { data: cmpA } = useQuery({
    queryKey: ['toast-margins-range', 'a', pair],
    enabled: !!pair && connected,
    queryFn: () => fetchMargins({ start: pair!.a.start, end: pair!.a.end }),
  });
  const { data: cmpB } = useQuery({
    queryKey: ['toast-margins-range', 'b', pair],
    enabled: !!pair && connected,
    queryFn: () => fetchMargins({ start: pair!.b.start, end: pair!.b.end }),
  });

  // Store comparison: the same margins per sibling store, side by side.
  const storeA = useQueries({
    queries: (connected && isMultiStore ? stores : []).map((s) => ({
      queryKey: ['toast-margins-store', 'a', s.id, pair?.a ?? 'window'],
      queryFn: () =>
        fetchMargins(
          pair
            ? { start: pair.a.start, end: pair.a.end, restaurantId: s.id }
            : { days: 28, restaurantId: s.id }
        ),
    })),
  });
  const storeB = useQueries({
    queries: (connected && isMultiStore && pair ? stores : []).map((s) => ({
      queryKey: ['toast-margins-store', 'b', s.id, pair?.b],
      queryFn: () => fetchMargins({ start: pair!.b.start, end: pair!.b.end, restaurantId: s.id }),
    })),
  });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;
  }

  const rows = margins?.rows || [];
  const unmatched = margins?.unmatched || [];

  // A failed request is not "not connected" — only say so when the API did.
  if (!margins) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-slate-900">Margins</h1>
        <p className="text-slate-500 mt-2">Margins are taking a moment to load — try again shortly.</p>
      </div>
    );
  }
  if (!margins.connected) {
    // No POS doesn't mean no sales: brands and makers sell through wholesale.
    // Compute the same table from wholesale orders × recipe costs.
    return <WholesaleMargins />;
  }

  const sumA = summarize(cmpA);
  const sumB = summarize(cmpB);
  const movers = cmpA && cmpB && sumA && sumB && sumA.windowDays > 0 && sumB.windowDays > 0
    ? profitMovers(cmpA, cmpB)
    : [];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Margins</h1>
        <p className="text-slate-500">What each product actually earns — your sales mix (last {margins.windowDays} days) × your recipe costs.</p>
      </div>

      {/* Period comparison — two real stored ranges, deltas only when both have data */}
      <ComparePeriodBar onChange={setPair} />
      {pair && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">Period comparison</h2>
            <span className="text-sm text-slate-500">
              {rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}
            </span>
          </div>
          {!cmpA || !cmpB || !sumA || !sumB ? (
            <p className="text-sm text-slate-500 p-6">Loading both periods…</p>
          ) : sumA.windowDays === 0 || sumB.windowDays === 0 ? (
            <div className="p-6 space-y-1">
              {sumA.windowDays === 0 && <p><NoData range={pair.a} /></p>}
              {sumB.windowDays === 0 && <p><NoData range={pair.b} /></p>}
              <p className="text-xs text-slate-400 pt-1">
                Deltas only show when both periods have recorded sales days — nothing is estimated or backfilled.
              </p>
            </div>
          ) : (
            (() => {
              // windowDays > 0 gives sales pace; profit + blended margin need
              // at least one costed item, else they rest on nothing.
              const aSales = sumA.windowDays > 0, bSales = sumB.windowDays > 0;
              const aCosted = aSales && sumA.costedItems > 0, bCosted = bSales && sumB.costedItems > 0;
              const barOrNull = (ok: boolean, v: number | null) => (ok ? v : null);
              const salesDetail: DetailLine[] = [{
                label: 'Recorded sales days',
                a: `${sumA.windowDays} days`,
                b: `${sumB.windowDays} days`,
              }];
              const costedDetail: DetailLine[] = [{
                label: 'Items costed / uncosted',
                a: `${sumA.costedItems} costed · ${sumA.uncostedItems} uncosted`,
                b: `${sumB.costedItems} costed · ${sumB.uncostedItems} uncosted`,
              }];
              return (
                <>
                  <CoverageBanner
                    a={{ sales: sumA.windowDays, expense: sumA.costedItems }}
                    b={{ sales: sumB.windowDays, expense: sumB.costedItems }}
                    aRange={pair.a}
                    bRange={pair.b}
                    salesNoun="sales"
                    expenseNoun="costed-recipe"
                    floor={0}
                  />
                  <div className="p-4">
                    <PairedBars
                      aLabel={rangeLabel(pair.a)}
                      bLabel={rangeLabel(pair.b)}
                      metrics={[
                        { label: 'Sales pace / week', a: barOrNull(aSales, sumA.salesWk), b: barOrNull(bSales, sumB.salesWk), kind: 'cents', goodWhenUp: true },
                        { label: 'Profit pace / week', a: barOrNull(aCosted, sumA.profitWk), b: barOrNull(bCosted, sumB.profitWk), kind: 'cents', goodWhenUp: true },
                        { label: 'Blended margin', a: barOrNull(aCosted, sumA.blendedPct), b: barOrNull(bCosted, sumB.blendedPct), kind: 'points', goodWhenUp: true },
                      ]}
                    />
                  </div>
                  <p className="text-xs text-slate-400 px-4 -mt-2 pb-2">Select a metric to see the records behind it.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="table-header">Metric</th>
                          <th className="table-header text-right">{rangeLabel(pair.a)}</th>
                          <th className="table-header text-right">{rangeLabel(pair.b)}</th>
                          <th className="table-header text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <GatedCompareRow label="Sales pace / week" a={sumA.salesWk} b={sumB.salesWk} kind="cents" goodWhenUp
                          aHasData={aSales} bHasData={bSales} noun="sales days" aRange={pair.a} bRange={pair.b} detail={salesDetail} />
                        <GatedCompareRow label="Profit pace / week" a={sumA.profitWk} b={sumB.profitWk} kind="cents" goodWhenUp
                          aHasData={aCosted} bHasData={bCosted} noun="costed-recipe sales" aRange={pair.a} bRange={pair.b} detail={costedDetail} />
                        <GatedCompareRow label="Blended margin" a={sumA.blendedPct} b={sumB.blendedPct} kind="points" goodWhenUp
                          aHasData={aCosted} bHasData={bCosted} noun="costed-recipe sales" aRange={pair.a} bRange={pair.b} />
                      </tbody>
                    </table>
                  </div>
              {movers.length > 0 && (
                <div className="px-4 pb-4">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-2 pt-3">What moved · weekly profit</p>
                  <DeltaBars
                    items={movers.map((m) => ({
                      name: m.name,
                      delta: m.now - m.prev,
                      detail: `${money(m.prev)} → ${money(m.now)}`,
                    }))}
                    format={signedShortMoney}
                  />
                </div>
              )}
              {sumA.coveragePct !== null && sumA.coveragePct < 99.5 && (
                <p className="text-xs text-slate-400 px-4 pb-1">
                  Blended margin is profit over the {sumA.coveragePct.toFixed(0)}% of sales that carry a recipe cost
                  ({money(sumA.costedSalesWk)} of {money(sumA.salesWk)}/week); items without a recipe sit in sales
                  but not in the margin, so profit ÷ sales reads a little under the margin.
                </p>
              )}
              <p className="text-xs text-slate-400 px-4 pb-4">
                Pace figures normalize each period to a week from its recorded days ({sumA.windowDays} and {sumB.windowDays}), so ranges of different lengths compare honestly.
              </p>
                </>
              );
            })()
          )}
        </div>
      )}

      {/* Store comparison — the same metric per store, side by side */}
      {isMultiStore && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">By store</h2>
            <span className="text-sm text-slate-500">
              {pair ? (
                <>{rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}</>
              ) : (
                `last ${margins.windowDays} days`
              )}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="table-header">Store</th>
                  <th className="table-header text-right">Sales / week</th>
                  <th className="table-header text-right">Profit / week</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                  <th className="table-header text-right">Blended margin</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stores.map((s, i) => {
                  const a = summarize(storeA[i]?.data);
                  const b = pair ? summarize(storeB[i]?.data) : null;
                  if (!a) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right text-slate-400" colSpan={pair ? 5 : 3}>Loading…</td>
                      </tr>
                    );
                  }
                  if (a.windowDays === 0) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right" colSpan={pair ? 5 : 3}><NoData range={pair?.a} /></td>
                      </tr>
                    );
                  }
                  const bOk = b && b.windowDays > 0 ? b : null;
                  return (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{s.name}</td>
                      <td className="table-cell text-right tabular-nums">{money(a.salesWk)}</td>
                      <td className="table-cell text-right tabular-nums font-semibold">{money(a.profitWk)}</td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.profitWk} prev={bOk.profitWk} kind="cents" goodWhenUp />
                          ) : b ? (
                            <NoData range={pair.b} />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell text-right tabular-nums">
                        {a.blendedPct === null ? '—' : `${a.blendedPct.toFixed(1)}%`}
                      </td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.blendedPct} prev={bOk.blendedPct} kind="points" goodWhenUp />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mix — top and bottom of the full list below */}
      <MixSection
        items={rows.map((r: any) => ({ name: r.toastName, marginPct: r.marginPct, weeklyProfitCents: r.weeklyProfitCents, soldPerWeek: r.soldPerWeek }))}
      />

      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-header text-left">Product</th>
                <th className="table-header text-right">Sold / week</th>
                <th className="table-header text-right">Avg price</th>
                <th className="table-header text-right">Cost</th>
                <th className="table-header text-right">Margin</th>
                <th className="table-header text-right">Margin %</th>
                <th className="table-header text-right">Profit / week</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r: any) => (
                <tr key={r.toastName}>
                  <td className="table-cell">
                    <span className="font-medium text-slate-900">{r.toastName}</span>
                    {r.menuName !== r.toastName && <span className="text-xs text-slate-400 ml-1.5">↔ {r.menuName}</span>}
                  </td>
                  <td className="table-cell text-right tabular-nums">{r.soldPerWeek.toLocaleString('en-US')}</td>
                  <td className="table-cell text-right tabular-nums">{money(r.avgPriceCents)}</td>
                  <td className="table-cell text-right tabular-nums">{money(r.costCents)}</td>
                  <td className="table-cell text-right tabular-nums">{money(r.marginCents)}</td>
                  <td className="table-cell text-right tabular-nums">
                    {r.marginPct === null ? '—' : (
                      <span className={clsx('font-medium', r.marginPct >= 70 ? 'text-emerald-700' : r.marginPct >= 40 ? 'text-slate-900' : 'text-red-600')}>
                        {r.marginPct}%
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-right tabular-nums font-semibold">{money(r.weeklyProfitCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-400 p-4">Costs come from recipes — estimated costs stay estimates until real supplier numbers replace them. Prices are your actual average selling price from the register.</p>
        </div>
      )}

      {unmatched.length > 0 && (
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Selling, but no recipe yet</p>
          <div className="space-y-1.5">
            {unmatched.map((u: any) => (
              <div key={u.toastName} className="flex items-baseline justify-between text-sm">
                <span className="text-slate-700">{u.toastName}</span>
                <span className="tabular-nums text-slate-500">{u.soldPerWeek.toLocaleString('en-US')}/wk · {money(u.avgPriceCents)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">These sell on your POS but aren't costed — add a recipe (or link an existing one) and they join the table above.</p>
        </div>
      )}

      {forecast?.ready && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-slate-400" />
            <p className="text-xs uppercase tracking-wide text-slate-400">Expected demand · next 7 days</p>
            <span className="text-xs text-slate-400 ml-auto">from {forecast.daysOfHistory} days of history, by weekday</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(forecast.days || []).map((d: any) => (
              <div key={d.date} className="rounded-lg bg-slate-50 p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="font-medium text-slate-900">{d.weekday}</p>
                  <p className="text-xs text-slate-500">{d.date} · ~{money(d.expectedGrossCents)}</p>
                </div>
                <div className="space-y-1">
                  {d.items.slice(0, 6).map((it: any) => (
                    <div key={it.name} className="flex items-baseline justify-between text-sm">
                      <span className="text-slate-600 truncate pr-2">{it.name}</span>
                      <span className="tabular-nums font-medium text-slate-900">{it.expectedQty}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">Weekday averages over your history — the starting point; holidays and weather come with the pilot.</p>
        </div>
      )}
    </div>
  );
}

// ===========================================
// WHOLESALE MARGINS (no POS — brands & makers)
// ===========================================
// The same honest table, sourced from wholesale orders (last 28 days) crossed
// with recipe costs. Products without a costed recipe show a dash, never a
// guessed margin.

const DAY_MS = 86400000;

// Aggregate orders inside a window into per-product rows. weeksOverride keeps
// the default table's long-standing "last 28 days = 4 weeks" math; explicit
// comparison windows derive weeks from their own length.
function aggregateWindow(
  list: any[],
  costByMenuItem: Map<string, number>,
  startMs: number,
  endMs: number,
  weeksOverride?: number,
) {
  const agg = new Map<string, { name: string; units: number; revenue: number; cost: number | null }>();
  let orderCount = 0;
  for (const o of list) {
    if (o.status === 'cancelled' || o.status === 'draft') continue;
    const when = new Date(o.fulfillmentDate || o.createdAt).getTime();
    if (!Number.isFinite(when) || when < startMs || when > endMs) continue;
    orderCount += 1;
    for (const item of o.items || []) {
      const key = item.menuItemId || item.name;
      const row = agg.get(key) || { name: item.name, units: 0, revenue: 0, cost: costByMenuItem.get(item.menuItemId) ?? null };
      row.units += Number(item.quantity) || 0;
      row.revenue += Number(item.totalPriceCents) || (Number(item.unitPriceCents) || 0) * (Number(item.quantity) || 0);
      agg.set(key, row);
    }
  }
  const weeks = weeksOverride ?? Math.max(1, (endMs - startMs) / (7 * DAY_MS));
  const rows = [...agg.values()]
    .filter((r) => r.units > 0)
    .map((r) => {
      const avgPrice = Math.round(r.revenue / r.units);
      const margin = r.cost !== null ? avgPrice - r.cost : null;
      return {
        ...r,
        avgPrice,
        margin,
        unitsPerWeek: Math.round(r.units / weeks),
        marginPct: margin !== null && avgPrice > 0 ? (margin / avgPrice) * 100 : null,
        profitWeek: margin !== null ? Math.round((margin * r.units) / weeks) : null,
      };
    })
    .sort((a, b) => (b.profitWeek ?? -1) - (a.profitWeek ?? -1));
  const totalRevenue = Math.round(rows.reduce((s, r) => s + r.revenue, 0));
  const totalProfit = Math.round(rows.reduce((s, r) => s + (r.margin !== null ? r.margin * r.units : 0), 0));
  // Costed vs uncosted rows separate a real profit figure from "selling, but
  // no recipe yet" — a profit delta is only honest when both sides have cost.
  const costedItems = rows.filter((r) => r.cost !== null).length;
  const uncostedItems = rows.length - costedItems;
  return { rows, orderCount, totalRevenue, totalProfit, costedItems, uncostedItems };
}

const windowMs = (r: DateRange) => ({
  start: new Date(`${r.start}T00:00:00`).getTime(),
  // inclusive end day
  end: new Date(`${r.end}T00:00:00`).getTime() + DAY_MS - 1,
});

function WholesaleMargins() {
  const [pair, setPair] = useState<ComparePair | null>(null);
  // US / EU / Combined — the same lens Channels uses; the entity of an order
  // is its customer's, filtered server-side.
  const [entity, setEntity] = useState<EntityView>('all');
  const { data: orders } = useQuery({
    queryKey: ['wholesale-margin-orders', entity],
    queryFn: async () => (await api.get('/v1/wholesale/orders', { params: entity === 'all' ? {} : { entity } })).data?.data,
  });
  const { data: recipes } = useQuery({
    queryKey: ['wholesale-margin-recipes'],
    queryFn: async () => (await api.get('/v1/recipes')).data?.data,
  });

  const fullList = orders?.orders || orders || [];
  // Recipe costs are USD, so the margin table only holds USD orders — euros
  // and kroner are never subtracted from dollars. Non-USD revenue is summed
  // natively and said out loud below instead of being silently blended.
  const list = fullList.filter((o: any) => String(o.currencyCode || 'USD').toUpperCase() === 'USD');
  const nonUsdBy: Record<string, number> = {};
  for (const o of fullList) {
    const cur = String(o.currencyCode || 'USD').toUpperCase();
    if (cur === 'USD' || o.status === 'cancelled' || o.status === 'draft') continue;
    const when = new Date(o.fulfillmentDate || o.createdAt).getTime();
    if (!Number.isFinite(when) || when < Date.now() - 28 * DAY_MS) continue;
    nonUsdBy[cur] = (nonUsdBy[cur] || 0) + (o.totalCents || 0);
  }
  const hasNonUsd = Object.keys(nonUsdBy).length > 0;
  const recipeList = recipes?.recipes || recipes || [];
  const costByMenuItem = new Map<string, number>(
    recipeList
      .filter((r: any) => r.menuItemId && r.costPerPortionCents > 0)
      .map((r: any) => [r.menuItemId, r.costPerPortionCents])
  );

  // Default view: the long-standing last-28-days window (no upper bound, so
  // orders fulfilled a few days out keep counting, as they always have).
  const main = aggregateWindow(list, costByMenuItem, Date.now() - 28 * DAY_MS, Number.POSITIVE_INFINITY, 4);
  const rows = main.rows;

  const cmpA = pair ? aggregateWindow(list, costByMenuItem, windowMs(pair.a).start, windowMs(pair.a).end) : null;
  const cmpB = pair ? aggregateWindow(list, costByMenuItem, windowMs(pair.b).start, windowMs(pair.b).end) : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Margins</h1>
          <p className="text-slate-500">
            What each product actually earns — your wholesale orders (last 28 days) × your recipe costs.
          </p>
        </div>
        <EntityToggle value={entity} onChange={setEntity} />
      </div>
      {hasNonUsd && (
        <div className="card p-4 text-sm text-slate-600">
          {formatByCurrency(nonUsdBy)} of the last 28 days' orders bill in their own currency — recipe costs are in USD,
          so those orders stay out of this table rather than mixing currencies. Their converted totals live on
          Wholesale → Channels, labeled with the day's ECB rate.
        </div>
      )}

      {/* Period comparison — two real stored windows of wholesale orders */}
      <ComparePeriodBar onChange={setPair} />
      {pair && cmpA && cmpB && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">Period comparison</h2>
            <span className="text-sm text-slate-500">
              {rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}
            </span>
          </div>
          {cmpA.orderCount === 0 || cmpB.orderCount === 0 ? (
            <div className="p-6 space-y-1">
              {cmpA.orderCount === 0 && <p><NoData range={pair.a} /></p>}
              {cmpB.orderCount === 0 && <p><NoData range={pair.b} /></p>}
              <p className="text-xs text-slate-400 pt-1">
                Deltas only show when both periods have wholesale orders — nothing is estimated or backfilled.
              </p>
            </div>
          ) : (
            (() => {
              // Revenue needs orders; profit needs at least one costed recipe,
              // else "profit" rests on uncosted lines.
              const aRev = cmpA.orderCount > 0, bRev = cmpB.orderCount > 0;
              const aProfit = aRev && cmpA.costedItems > 0, bProfit = bRev && cmpB.costedItems > 0;
              const barOrNull = (ok: boolean, v: number | null) => (ok ? v : null);
              const revDetail: DetailLine[] = [{
                label: 'Wholesale orders counted',
                a: `${cmpA.orderCount} orders`,
                b: `${cmpB.orderCount} orders`,
              }];
              const profitDetail: DetailLine[] = [{
                label: 'Items costed / uncosted',
                a: `${cmpA.costedItems} costed · ${cmpA.uncostedItems} uncosted`,
                b: `${cmpB.costedItems} costed · ${cmpB.uncostedItems} uncosted`,
              }];
              return (
                <>
                  <CoverageBanner
                    a={{ sales: cmpA.orderCount, expense: cmpA.costedItems }}
                    b={{ sales: cmpB.orderCount, expense: cmpB.costedItems }}
                    aRange={pair.a}
                    bRange={pair.b}
                    salesNoun="orders"
                    expenseNoun="costed-recipe"
                    floor={0}
                  />
                  <div className="p-4">
                    <PairedBars
                      aLabel={rangeLabel(pair.a)}
                      bLabel={rangeLabel(pair.b)}
                      metrics={[
                        { label: 'Wholesale revenue', a: barOrNull(aRev, cmpA.totalRevenue), b: barOrNull(bRev, cmpB.totalRevenue), kind: 'cents', goodWhenUp: true },
                        { label: 'Profit (costed items)', a: barOrNull(aProfit, cmpA.totalProfit), b: barOrNull(bProfit, cmpB.totalProfit), kind: 'cents', goodWhenUp: true },
                      ]}
                    />
                  </div>
                  <p className="text-xs text-slate-400 px-4 -mt-2 pb-2">Select a metric to see the records behind it.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="table-header">Metric</th>
                          <th className="table-header text-right">{rangeLabel(pair.a)}</th>
                          <th className="table-header text-right">{rangeLabel(pair.b)}</th>
                          <th className="table-header text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <GatedCompareRow label="Wholesale revenue" a={cmpA.totalRevenue} b={cmpB.totalRevenue} kind="cents" goodWhenUp
                          aHasData={aRev} bHasData={bRev} noun="orders" aRange={pair.a} bRange={pair.b} detail={revDetail} />
                        <GatedCompareRow label="Profit (costed items)" a={cmpA.totalProfit} b={cmpB.totalProfit} kind="cents" goodWhenUp
                          aHasData={aProfit} bHasData={bProfit} noun="costed-recipe orders" aRange={pair.a} bRange={pair.b} detail={profitDetail} />
                        <tr>
                          <td className="table-cell font-medium text-slate-700">Orders</td>
                          <td className="table-cell text-right tabular-nums">{cmpA.orderCount}</td>
                          <td className="table-cell text-right tabular-nums text-slate-500">{cmpB.orderCount}</td>
                          <td className="table-cell text-right">
                            <span className="text-sm text-slate-500 tabular-nums">
                              {cmpA.orderCount - cmpB.orderCount >= 0 ? '+' : ''}{cmpA.orderCount - cmpB.orderCount}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-400 px-4 pb-4">
                    Totals cover each selected range exactly as stored — ranges of different lengths are shown as-is, never scaled.
                  </p>
                </>
              );
            })()
          )}
        </div>
      )}

      {/* Mix — top and bottom of the full list below */}
      <MixSection
        items={rows.map((r) => ({ name: r.name, marginPct: r.marginPct, weeklyProfitCents: r.profitWeek, soldPerWeek: r.units / 4 }))}
      />

      {rows.length === 0 ? (
        <div className="card p-6">
          <p className="text-slate-500">
            Margins appear once wholesale orders and costed recipes exist — orders bring the
            selling price, recipes bring the cost.
          </p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-header text-left">Product</th>
                <th className="table-header text-right">Sold / week</th>
                <th className="table-header text-right">Avg price</th>
                <th className="table-header text-right">Cost</th>
                <th className="table-header text-right">Margin</th>
                <th className="table-header text-right">Margin %</th>
                <th className="table-header text-right">Profit / week</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.name}>
                  <td className="table-cell font-medium text-slate-900">{r.name}</td>
                  <td className="table-cell text-right">{r.unitsPerWeek}</td>
                  <td className="table-cell text-right">{money(r.avgPrice)}</td>
                  <td className="table-cell text-right">{money(r.cost)}</td>
                  <td className="table-cell text-right">{money(r.margin)}</td>
                  <td className={clsx('table-cell text-right font-medium', r.marginPct !== null && r.marginPct < 30 ? 'text-amber-600' : 'text-emerald-700')}>
                    {r.marginPct !== null ? `${r.marginPct.toFixed(0)}%` : '—'}
                  </td>
                  <td className="table-cell text-right font-semibold">{money(r.profitWeek)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5" />
        Products without a costed recipe show a dash — link a recipe and the margin fills in.
      </p>
    </div>
  );
}
